/**
 * CoWork folder tree — a lazy, browsable view of the working folder (hybrid
 * CoWork, "Working folders"). Lists one directory level at a time via the device
 * bridge; expanding a folder loads its children; clicking a file opens it in the
 * preview; the ↗ button reveals a folder in the OS explorer. Auto-reloads when
 * the change set updates (`nonce`), and auto-expands + highlights + scrolls to
 * the file the agent is currently working on (`activePath`). Fills its parent
 * and scrolls internally. Desktop-only (rendered inside the Context panel).
 *
 * @module components/cowork/cowork-folder-tree
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, File, Folder, FolderOpen, Loader2 } from "lucide-react";

import { getDesktop, type FileEntry } from "@/lib/desktop";
import { cn } from "@/lib/utils";

const sortEntries = (a: FileEntry, b: FileEntry) =>
  Number(b.isDirectory) - Number(a.isDirectory) || a.name.localeCompare(b.name);

/** Directory prefixes of a file path, e.g. "a/b/c.ts" → ["a", "a/b"]. */
const ancestorsOf = (path: string): string[] => {
  const parts = path.split("/").filter(Boolean);
  const out: string[] = [];
  let acc = "";
  for (let i = 0; i < parts.length - 1; i++) {
    acc = acc ? `${acc}/${parts[i]}` : parts[i];
    out.push(acc);
  }
  return out;
};

export interface CoworkFolderTreeHandle {
  refresh: () => void;
}

export const CoworkFolderTree = forwardRef<
  CoworkFolderTreeHandle,
  {
    rootName: string | null;
    onOpenFile: (path: string) => void;
    onReveal?: (path: string, isDir: boolean) => void;
    nonce?: number;
    activePath?: string | null;
    /** Reports whether a (re)load is in flight, so the parent can spin its icon. */
    onBusyChange?: (busy: boolean) => void;
  }
>(function CoworkFolderTree({ rootName, onOpenFile, onReveal, nonce = 0, activePath = null, onBusyChange }, ref) {
  const [children, setChildren] = useState<Record<string, FileEntry[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["."]));
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const childrenRef = useRef<Record<string, FileEntry[]>>({});
  childrenRef.current = children;

  // `silent` skips the loading spinner — used by the auto-refresh (poll + change
  // events) so the tree updates live without the refresh icon flashing.
  const load = useCallback(async (dirPath: string, force = false, silent = false) => {
    if (!force && childrenRef.current[dirPath]) return;
    if (!silent) setLoading((s) => new Set(s).add(dirPath));
    let entries: FileEntry[] = [];
    try {
      const res = await getDesktop()?.cowork.listFiles(dirPath);
      entries = (res?.entries ?? []).slice().sort(sortEntries);
    } catch {
      entries = []; // never leave a row stuck on "loading…"
    }
    setChildren((c) => ({ ...c, [dirPath]: entries }));
    if (!silent) {
      setLoading((s) => {
        const n = new Set(s);
        n.delete(dirPath);
        return n;
      });
    }
  }, []);

  /** Re-fetch every directory that's currently loaded (keeps expansion). */
  const refresh = useCallback(
    async (silent = false) => {
      const dirs = Object.keys(childrenRef.current);
      await Promise.all((dirs.length ? dirs : ["."]).map((d) => load(d, true, silent)));
    },
    [load],
  );

  useEffect(() => {
    void load(".");
  }, [load]);

  // Expose refresh to the parent (the button lives on the section heading).
  useImperativeHandle(ref, () => ({ refresh: () => void refresh() }), [refresh]);

  // Report busy state so the parent's refresh icon can spin during a load.
  useEffect(() => {
    onBusyChange?.(loading.size > 0);
  }, [loading, onBusyChange]);

  // Auto-refresh when the agent's change set updates (new files/folders appear).
  const firstNonce = useRef(true);
  useEffect(() => {
    if (firstNonce.current) {
      firstNonce.current = false;
      return;
    }
    void refresh(true);
  }, [nonce, refresh]);

  // Belt-and-suspenders: poll quietly so the tree stays live even if a change
  // event is missed — no refresh-button click needed.
  useEffect(() => {
    const id = setInterval(() => void refresh(true), 4000);
    return () => clearInterval(id);
  }, [refresh]);

  // Reveal the file the agent is working on: expand every ancestor folder and
  // load any that aren't loaded yet, so the active row renders (+ scrolls into view).
  useEffect(() => {
    if (!activePath) return;
    const ancestors = ancestorsOf(activePath);
    setExpanded((s) => {
      const n = new Set(s);
      n.add(".");
      ancestors.forEach((a) => n.add(a));
      return n;
    });
    ancestors.forEach((a) => {
      if (!childrenRef.current[a]) void load(a);
    });
  }, [activePath, load]);

  const toggle = (dirPath: string) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(dirPath)) {
        n.delete(dirPath);
      } else {
        n.add(dirPath);
        if (!childrenRef.current[dirPath]) void load(dirPath);
      }
      return n;
    });
  };

  const renderLevel = (dirPath: string, depth: number): React.ReactNode => {
    // Hard backstop against runaway recursion (e.g. a listing that ever contains
    // itself) so a bad response can never crash the whole panel.
    if (depth > 20) return null;
    const entries = children[dirPath];
    const pad = depth * 12 + 24;
    if (!entries) {
      return loading.has(dirPath) ? (
        <div className="flex items-center gap-2 py-1 text-[11px] text-muted-foreground" style={{ paddingLeft: pad }}>
          <Loader2 className="size-3 animate-spin" /> loading…
        </div>
      ) : null;
    }
    if (entries.length === 0) {
      return (
        <div className="py-1 text-[11px] text-muted-foreground/70" style={{ paddingLeft: pad }}>
          empty
        </div>
      );
    }
    return entries.map((e) =>
      e.isDirectory ? (
        <div key={e.path}>
          <Row
            depth={depth}
            folder
            open={expanded.has(e.path)}
            name={e.name}
            onClick={() => toggle(e.path)}
            onReveal={onReveal ? () => onReveal(e.path, true) : undefined}
          />
          {expanded.has(e.path) && renderLevel(e.path, depth + 1)}
        </div>
      ) : (
        <Row key={e.path} depth={depth} name={e.name} active={e.path === activePath} onClick={() => onOpenFile(e.path)} />
      ),
    );
  };

  return (
    // Content-height (no flex-1/scroll of its own) so the Working-folders section
    // sizes to the tree; the parent scroll region handles overflow.
    <div className="pb-2">
      <Row
        depth={0}
        folder
        open={expanded.has(".")}
        name={rootName ?? "folder"}
        onClick={() => toggle(".")}
        onReveal={onReveal ? () => onReveal(".", true) : undefined}
      />
      {expanded.has(".") && renderLevel(".", 1)}
    </div>
  );
});

function Row({
  depth,
  folder,
  open,
  name,
  active,
  onClick,
  onReveal,
}: {
  depth: number;
  folder?: boolean;
  open?: boolean;
  name: string;
  active?: boolean;
  onClick: () => void;
  onReveal?: () => void;
}) {
  const FolderIcon = open ? FolderOpen : Folder;
  const Chevron = open ? ChevronDown : ChevronRight;
  // Scroll the active file into view when it becomes active.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ block: "nearest" });
  }, [active]);
  return (
    <div
      ref={ref}
      className={cn(
        "group flex items-center rounded-md pr-1 transition-colors hover:bg-accent/60",
        active && "bg-secondary/15",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        title={name}
        className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left"
        style={{ paddingLeft: `${depth * 12 + 6}px` }}
      >
        {folder ? (
          <>
            <Chevron className="size-3.5 shrink-0 text-muted-foreground" />
            <FolderIcon className="size-4 shrink-0 text-secondary" />
          </>
        ) : (
          <>
            <span className="w-3.5 shrink-0" />
            <File className={cn("size-4 shrink-0", active ? "text-secondary" : "text-muted-foreground")} />
          </>
        )}
        <span
          className={cn(
            "truncate text-[13px]",
            folder ? "font-medium text-foreground" : active ? "font-medium text-secondary" : "text-foreground/90",
          )}
        >
          {name}
        </span>
      </button>
      {folder && onReveal && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReveal();
          }}
          title="Open in file explorer"
          className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-all hover:text-foreground group-hover:opacity-100"
        >
          <ExternalLink className="size-3" />
        </button>
      )}
    </div>
  );
}
